import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { safeReturnTo } from '@utils/returnTo';
import { AxiosError } from 'axios';
import { Eye, EyeOff } from 'lucide-react';
import { useToast } from '@centraoneplus/ui';
import { useAuthStore } from '@stores/authStore';
import { authService, isMfaChallenge } from '@services/auth.service';
import { CredentialEncryptionError } from '@/lib/credentialEncryption';

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

// "Remember me" persists only the user's email (never the password) so the
// login form can be prefilled on a future visit. Session persistence is not
// tied to this checkbox: tokens always live in localStorage (see tokenStorage)
// so a signed-in user survives a tab close either way.
const REMEMBERED_EMAIL_KEY = 'rememberedEmail';

const getRememberedEmail = (): string => {
  try {
    return localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
};

const setRememberedEmail = (value: string): void => {
  try {
    localStorage.setItem(REMEMBERED_EMAIL_KEY, value);
  } catch {
    // Storage may be unavailable (e.g. private mode); never block login.
  }
};

const clearRememberedEmail = (): void => {
  try {
    localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  } catch {
    // Ignore storage errors.
  }
};

export default function LoginPage() {
  const [email, setEmail] = useState(() => getRememberedEmail());
  const [rememberMe, setRememberMe] = useState(() => getRememberedEmail() !== '');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Email-OTP challenge state (login step 2).
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoNotice, setSsoNotice] = useState('');
  // Microsoft's callback bounces back here with ?ssoError=<reason> when it
  // cannot complete. Read once at mount; the param is stripped below.
  const [ssoError] = useState(() => {
    const reason = new URLSearchParams(window.location.search).get('ssoError');
    switch (reason) {
      case 'no_account':
        return 'That Microsoft account is not linked to a CentraOne user. Ask your administrator to invite you first.';
      case 'account_inactive':
        return 'Your account is not active. Please contact your administrator.';
      case 'ambiguous_account':
        return 'That email address exists in more than one organisation, so we cannot tell which to sign you into. Please contact your administrator.';
      case 'access_denied':
        return 'Microsoft sign-in was cancelled.';
      case 'not_configured':
        return 'Microsoft sign-in is not available. Please use your password.';
      case 'microsoft_error':
      case 'sso_failed':
        return 'Microsoft sign-in could not be completed. Please try again or use your password.';
      default:
        return '';
    }
  });
  const [mfaToken, setMfaToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const toast = useToast();

  // When the API client force-logs-out a session it redirects here with a
  // ?reason=. Capture the notice ONCE at mount, then strip the param from the
  // URL so it doesn't survive a refresh or a navigate-away-and-back (the login
  // notice/error must never persist in the URL or any storage).
  const [logoutNotice] = useState(() => {
    const reason = new URLSearchParams(window.location.search).get('reason');
    if (reason === 'account_deactivated')
      return 'Your account has been deactivated. Please contact your administrator.';
    if (reason === 'account_inactive')
      return 'Your account is no longer active. Please contact your administrator.';
    if (reason === 'session_expired')
      return 'Your session has ended. Please sign in again.';
    if (reason === 'idle_timeout')
      return 'You were signed out after 30 minutes of inactivity.';
    // Set by MainLayout's sign-out, which reloads the document (rather than
    // routing here) so no previous session's data survives in memory.
    if (reason === 'signed_out') return 'You have been signed out.';
    return '';
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('reason') || params.has('ssoError')) {
      params.delete('reason');
      params.delete('ssoError');
      const qs = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (qs ? `?${qs}` : ''),
      );
    }
  }, []);


  // Countdown for the emailed code. Ticks once per second while the OTP step is
  // active; when it reaches 0 the code has expired and the user must resend.
  useEffect(() => {
    if (!requiresMfa || secondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [requiresMfa, secondsLeft]);

  const expired = requiresMfa && secondsLeft <= 0;
  const timerLabel = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  // Step 1 — validate password; on success the server emails a code and returns
  // an MFA challenge. We switch to the OTP view and start the 10-minute countdown.
  const handlePasswordSubmit = async () => {
    try {
      const result = await authService.login({ email, password });

      if (isMfaChallenge(result)) {
        setMfaToken(result.mfaToken);
        setMaskedEmail(result.maskedEmail);
        setSecondsLeft(result.expiresInSeconds || 600);
        setMfaCode('');
        setRequiresMfa(true);
        setError('');
        toast.success('Code sent', `We emailed a code to ${result.maskedEmail}`);
        return;
      }

      // Defensive: full session returned (no-MFA path is retired).
      completeLogin(result);
    } catch (err) {
      handleLoginError(err);
    }
  };

  // Step 2 — verify the emailed code and finish login.
  const handleOtpSubmit = async () => {
    if (expired) {
      setError('Your code has expired. Please request a new one.');
      return;
    }
    try {
      const result = await authService.verifyLoginOtp(mfaToken, mfaCode);
      completeLogin(result);
    } catch (err) {
      const axiosError = err as AxiosError<{ error: ApiError }>;
      const code = axiosError.response?.data?.error?.code;
      if (code === 'OTP_EXPIRED' || code === 'OTP_NOT_FOUND' || code === 'OTP_MAX_ATTEMPTS') {
        setSecondsLeft(0);
        const msg =
          code === 'OTP_MAX_ATTEMPTS'
            ? 'Too many incorrect attempts. Please request a new code.'
            : 'Your code has expired. Please request a new one.';
        setError(msg);
        toast.error('Code no longer valid', msg);
      } else if (code === 'MFA_TOKEN_INVALID') {
        // The login session (mfaToken) lapsed — send them back to step 1.
        handleBackToLogin();
        setError('Your login session has expired. Please sign in again.');
        toast.error('Session expired', 'Please sign in again.');
      } else {
        // Wrong code — pop an error message asking for the correct code.
        setMfaCode('');
        setError('Enter the correct code.');
        toast.error('Incorrect code', 'Enter the correct code.');
      }
    }
  };

  // "Sign in with Microsoft". No email needed: we hand the browser straight to
  // Microsoft, which owns the account picker, the password prompt and MFA. We
  // never see the Microsoft password and never inspect the browser's existing
  // Microsoft session — Entra recognises its own.
  const handleMicrosoftLogin = async () => {
    setError('');
    setSsoNotice('');
    setSsoLoading(true);
    try {
      const result = await authService.getMicrosoftAuthorizeUrl();

      if (!result.configured || !result.authorizeUrl) {
        setSsoNotice(
          'Microsoft sign-in is not set up for this environment yet. Please sign in with your password, or contact your administrator.',
        );
        return;
      }

      // The Microsoft round trip is a full navigation, so settle the email
      // prefill before leaving the page.
      const trimmed = email.trim();
      if (rememberMe && trimmed) {
        setRememberedEmail(trimmed);
      } else if (!rememberMe) {
        clearRememberedEmail();
      }

      // Full navigation, not fetch: this leaves our origin for Microsoft.
      window.location.assign(result.authorizeUrl);
    } catch {
      setSsoNotice('');
      setError('Could not start Microsoft sign-in. Please try again or use your password.');
    } finally {
      setSsoLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (requiresMfa) {
        await handleOtpSubmit();
      } else {
        await handlePasswordSubmit();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResending(true);
    try {
      const result = await authService.resendLoginOtp(mfaToken);
      setMfaToken(result.mfaToken || mfaToken);
      setMaskedEmail(result.maskedEmail || maskedEmail);
      setSecondsLeft(result.expiresInSeconds || 600);
      setMfaCode('');
      toast.success('New code sent', `We emailed a new code to ${result.maskedEmail || maskedEmail}`);
    } catch (err) {
      const axiosError = err as AxiosError<{ error: ApiError }>;
      const code = axiosError.response?.data?.error?.code;
      if (code === 'OTP_RESEND_COOLDOWN') {
        setError(axiosError.response?.data?.error?.message || 'Please wait a moment before requesting another code.');
      } else if (code === 'MFA_TOKEN_INVALID') {
        handleBackToLogin();
        setError('Your login session has expired. Please sign in again.');
      } else {
        setError('Could not send a new code. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const completeLogin = (result: { user: any; accessToken: string }) => {
    const { user, accessToken } = result;
    login(
      {
        id: user.id,
        email: user.email,
        name: user.name,
        avatarUrl: user.avatarUrl,
        role: user.role,
        tenantId: user.tenantId,
      },
      accessToken,
    );
    if (rememberMe) {
      setRememberedEmail(email.trim());
    } else {
      clearRememberedEmail();
    }
    toast.success('Welcome back!', `Signed in as ${user.name}`);
    // Back to where sign-in was required (e.g. HuHoKa CRM's approve page), if
    // that is a same-origin path; otherwise the dashboard.
    navigate(safeReturnTo(new URLSearchParams(window.location.search).get('returnTo')) ?? '/dashboard');
  };

  const handleLoginError = (err: unknown) => {
    // e.g. the page was opened over http://, so the password can't be encrypted
    if (err instanceof CredentialEncryptionError) {
      setError(err.message);
      return;
    }
    // The BFF's own errors (such as credential encryption) are { code, message }.
    const axiosError = err as AxiosError<{ error?: ApiError; message?: string }>;
    if (axiosError.response?.status === 401) {
      const code = axiosError.response.data?.error?.code;
      if (code === 'ACCOUNT_DELETED') {
        setError('Your account has been deleted. Please contact your administrator if you believe this is an error.');
      } else if (code === 'ACCOUNT_DEACTIVATED') {
        setError('Your account has been deactivated. Please contact your administrator for assistance.');
      } else {
        setError('Invalid email or password.');
      }
    } else if (axiosError.response?.status === 429) {
      setError('Too many login attempts. Please try again later.');
    } else if (axiosError.response?.data?.error?.message) {
      setError(axiosError.response.data.error.message);
    } else if (typeof axiosError.response?.data?.message === 'string') {
      setError(axiosError.response.data.message);
    } else {
      setError('An error occurred. Please try again.');
    }
  };

  const handleBackToLogin = () => {
    setRequiresMfa(false);
    setMfaCode('');
    setMfaToken('');
    setMaskedEmail('');
    setSecondsLeft(0);
    setError('');
  };

  return (
    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
      {logoutNotice && !error && !ssoError && (
        <div className="px-4 py-3 rounded-[10px] bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300">
          {logoutNotice}
        </div>
      )}
      {/* Why a Microsoft sign-in attempt bounced back here. */}
      {ssoError && !error && (
        <div className="px-4 py-3 rounded-[10px] bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
          {ssoError}
        </div>
      )}
      {error && (
        <div className="px-4 py-3 rounded-[10px] bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {!requiresMfa ? (
          <>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input !rounded-[10px] mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              {/* Hide the native Edge/IE password reveal control so only the custom toggle shows */}
              <style>{`#password::-ms-reveal, #password::-ms-clear { display: none; }`}</style>
              <div className="relative mt-1">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input !rounded-[10px] pr-10 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-800 dark:text-white hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div>
            <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Enter verification code
            </label>
            <p className="mt-1 mb-2 text-sm text-gray-500 dark:text-gray-400">
              We sent a 6-digit code to <span className="font-medium text-gray-700 dark:text-gray-200">{maskedEmail}</span>.
            </p>
            <input
              id="mfaCode"
              name="mfaCode"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              required
              disabled={expired}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="input !rounded-[10px] mt-1 text-center text-2xl tracking-widest dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 disabled:opacity-50"
              placeholder="000000"
              maxLength={6}
            />
            <div className="mt-2 flex items-center justify-between text-sm">
              {expired ? (
                <span className="text-red-600 dark:text-red-400">Code expired</span>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">
                  Code expires in <span className="font-medium tabular-nums">{timerLabel}</span>
                </span>
              )}
              <button
                type="button"
                onClick={handleResend}
                disabled={resending || (!expired && secondsLeft > 0)}
                className="text-[var(--co-brass-ink)] hover:text-[var(--co-brass)] disabled:opacity-40 disabled:cursor-not-allowed"
                title={!expired && secondsLeft > 0 ? 'You can resend once the code expires' : 'Send a new code'}
              >
                {resending ? 'Sending…' : 'Resend code'}
              </button>
            </div>
          </div>
        )}
      </div>

      {!requiresMfa && (
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 accent-[var(--co-brass)] border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Remember me
            </label>
          </div>

          <Link to="/forgot-password" className="text-sm text-[var(--co-brass-ink)] hover:text-[var(--co-brass)]">
            Forgot password?
          </Link>
        </div>
      )}

      <div className="space-y-3">
        <button
          type="submit"
          disabled={loading || (requiresMfa && (mfaCode.length !== 6 || expired))}
          className="btn-primary w-full !rounded-[10px] h-11 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Signing in...' : requiresMfa ? 'Verify' : 'Sign in'}
        </button>

        {requiresMfa && (
          <button
            type="button"
            onClick={handleBackToLogin}
            className="btn-secondary w-full !rounded-[10px] h-11 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Back to login
          </button>
        )}
      </div>

      {/* Single sign-on, offered alongside the password form (never instead of
          it). Hidden only during the OTP step, which belongs to the password
          flow. Shown regardless of backend configuration: hiding it made an
          unconfigured environment indistinguishable from a broken deploy, and
          a clear message on click is more useful than a missing control. */}
      {!requiresMfa && (
        <div className="space-y-3">
          {/* Rules on either side of the label rather than a label sitting on
              an opaque chip: the chip's hard-coded white / gray-800 background
              does not match the card's surface, so it showed as a patch. */}
          <div className="flex items-center gap-3" aria-hidden="true">
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            <span className="text-xs text-[var(--co-ink-3)]">or</span>
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          </div>

          <button
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={ssoLoading || loading}
            className="w-full inline-flex h-11 items-center justify-center gap-3 rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-4 text-sm font-medium text-[var(--co-ink)] transition-colors hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {/* Microsoft's four-square mark, per their branding guidance. */}
            <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            {ssoLoading ? 'Redirecting…' : 'Sign in with Microsoft'}
          </button>

          {ssoNotice && (
            <p className="text-center text-xs text-gray-500 dark:text-gray-400">{ssoNotice}</p>
          )}
        </div>
      )}
    </form>
  );
}
